import { colors } from "theme/colors";

// Allstar colours only, so a burst reads as ours rather than as party confetti.
const CONFETTI_COLORS = [
  colors.envy,
  colors.ice,
  colors.chalk,
  colors.tierPlat,
];

const MUTE_KEY = "rewards.muted";

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

export const isMuted = (): boolean => {
  try {
    return window.localStorage.getItem(MUTE_KEY) === "1";
  } catch (e) {
    return false;
  }
};

export const setMuted = (muted: boolean) => {
  try {
    window.localStorage.setItem(MUTE_KEY, muted ? "1" : "0");
  } catch (e) {
    // A blocked store only costs the preference, not the celebration.
  }
};

type Piece = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  spin: number;
  angle: number;
  color: string;
};

// Synthesised rather than sampled: no asset to ship, no licence to clear, and
// it is tuned to the same two-note rise the UI animates on.
export const playClaimCue = () => {
  if (isMuted() || prefersReducedMotion()) return;

  const Ctor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return;

  try {
    const ctx = new Ctor();
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.09, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.42);

    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(660, now);
    osc.frequency.setValueAtTime(880, now + 0.09);
    osc.frequency.exponentialRampToValueAtTime(1320, now + 0.34);
    osc.connect(gain);
    osc.start(now);
    osc.stop(now + 0.45);
    osc.onended = () => ctx.close();
  } catch (e) {
    // Audio is a nicety; a blocked context must never break the claim.
  }
};

// A burst from the element that earned it, so the celebration is attached to
// the thing the player pressed rather than thrown at the whole page.
export const burstFrom = (origin?: DOMRect | null) => {
  if (prefersReducedMotion()) return;

  const canvas = document.createElement("canvas");
  const dpr = window.devicePixelRatio || 1;
  canvas.width = window.innerWidth * dpr;
  canvas.height = window.innerHeight * dpr;
  canvas.style.cssText =
    "position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:2147483647";
  document.body.appendChild(canvas);

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    canvas.remove();
    return;
  }
  ctx.scale(dpr, dpr);

  const x = origin ? origin.left + origin.width / 2 : window.innerWidth / 2;
  const y = origin ? origin.top + origin.height / 2 : window.innerHeight / 3;

  const pieces: Piece[] = Array.from({ length: 90 }, () => {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 2.1;
    const speed = 5 + Math.random() * 9;
    return {
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size: 4 + Math.random() * 5,
      spin: (Math.random() - 0.5) * 0.3,
      angle: Math.random() * Math.PI,
      color:
        CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    };
  });

  const started = performance.now();
  const tick = (now: number) => {
    const elapsed = now - started;
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    ctx.globalAlpha = Math.max(0, 1 - elapsed / 2200);

    pieces.forEach((piece) => {
      piece.vy += 0.24;
      piece.vx *= 0.99;
      piece.vy *= 0.99;
      piece.x += piece.vx;
      piece.y += piece.vy;
      piece.angle += piece.spin;

      ctx.save();
      ctx.translate(piece.x, piece.y);
      ctx.rotate(piece.angle);
      ctx.fillStyle = piece.color;
      // Squashed on the spin, so each piece reads as a flake with a face.
      ctx.fillRect(
        -piece.size / 2,
        -piece.size / 2,
        piece.size,
        piece.size * Math.abs(Math.cos(piece.angle)),
      );
      ctx.restore();
    });

    if (elapsed < 2200) {
      requestAnimationFrame(tick);
    } else {
      canvas.remove();
    }
  };

  requestAnimationFrame(tick);
};

export const celebrate = (origin?: DOMRect | null) => {
  burstFrom(origin);
  playClaimCue();
};
